import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import logoIcon from '../assets/logo-icon-exact.png';
import { supabase } from '../supabaseClient';
import SubscribeModal from '../components/SubscribeModal';
import Footer from '../components/Footer';

function Home({ session, darkMode, toggleDark, isSubscribed }) {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');

  const validatePromoCode = async (rawCode) => {
    const code = rawCode.trim().toUpperCase();
    if (!code) {
      setPromoError('Please enter a code');
      return false;
    }

    setPromoError('');
    const { data, error } = await supabase
      .from('promo_codes')
      .select('code, active, expires_at, max_uses, current_uses')
      .eq('code', code)
      .single();

    if (error || !data || !data.active) {
      setPromoError('Promo code not recognised. Please check your code and try again.');
      return false;
    }

    if (data.expires_at && new Date(data.expires_at) < new Date()) {
      setPromoError('This promo code has expired.');
      return false;
    }

    if (data.max_uses !== null && data.current_uses >= data.max_uses) {
      setPromoError('This promo code has already been used up.');
      return false;
    }

    return true;
  };

  const handlePromoRedeem = async () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) {
      setPromoError('Please enter a code');
      return;
    }
    const isValid = await validatePromoCode(code);
    if (isValid) {
      navigate('/subscribe?code=' + encodeURIComponent(code));
    }
  };

  const handleSubscribeClick = async () => {
    const code = promoCode.trim().toUpperCase();
    if (code) {
      const isValid = await validatePromoCode(code);
      if (!isValid) return;
      navigate('/subscribe?code=' + encodeURIComponent(code));
      return;
    }

    navigate('/subscribe');
  };

  return (
    <div className="home-page">
      <header>
        <div className="logo" onClick={() => navigate(session ? '/quiz' : '/home')}>
          <div className="logo-icon"><img src={logoIcon} alt="" /></div>
          <div className="logo-text">TradeReady<span>Pro</span></div>
        </div>
        <div className="header-right">
          {!session && <Link to="/login" className="dark-toggle">Login</Link>}
          {!session && <button className="btn-subscribe" onClick={handleSubscribeClick}>Subscribe</button>}
          <Link to="/settings" className="dark-toggle" title="Settings">⚙️</Link>
        </div>
      </header>

      <div className="home-hero-wrap">
        <div className="home-hero-content">
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)',
              color: 'var(--gold)', padding: '4px 14px', borderRadius: 20,
              fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.06em',
              textTransform: 'uppercase', marginBottom: '1rem'
            }}>
              🇨🇦 Ontario Trades Exam Prep Platform
            </div>
            <h1>
              Pass Your <span>Trades Exam.</span><br />First Try.
            </h1>

            <section className="home-video-section" aria-labelledby="home-video-title">
              <div className="home-video-card home-video-card-compact">
                <div className="home-video-copy">
                  <div className="home-video-kicker">Quick Walkthrough</div>
                  <h2 id="home-video-title">See How TradeReady Pro Works</h2>
                </div>

                <div className="home-video-frame-wrap">
                  <iframe
                    src="https://www.youtube-nocookie.com/embed/Xf4nBl3jApA?rel=0"
                    title="TradeReady Pro tutorial"
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
              </div>
            </section>

            <p className="home-hero-sub">
              2,093+ electrical practice questions for Master Electrician, 309A, 442A, and 309C exams.
              Aligned with OESC (Master), CEC-based section prep, OHSA, and Ontario Acts & Regulations.
              Every answer explained. Every rule referenced.
            </p>

            <div className="home-stats">
              <div className="home-stat">
                <strong>2,093+</strong>
                <span>Questions</span>
              </div>
              <div className="home-stat">
                <strong>Timed</strong>
                <span>Exam Simulator</span>
              </div>
              <div className="home-stat">
                <strong>100%</strong>
                <span>OESC &amp; CEC Focused</span>
              </div>
            </div>

            <div className="home-cta">
              {!session && (
                <button className="btn-hero-outline" onClick={handleSubscribeClick}>
                  Unlock All Features
                </button>
              )}
              {!session && <span style={{ alignSelf: 'center', fontWeight: 700, opacity: 0.75 }}>OR</span>}
              <Link to="/quiz" className="btn-hero-primary">Start Free Practice (limits apply) →</Link>
            </div>

            {!session && (
              <div className="home-promo-section">
                <div className="home-promo-label">Have a promo code?</div>
                <div className="home-promo-input-group">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => {
                      setPromoCode(e.target.value.toUpperCase());
                      setPromoError('');
                    }}
                    placeholder="Enter code here"
                    className="home-promo-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handlePromoRedeem();
                      }
                    }}
                  />
                  <button
                    className="home-promo-btn"
                    onClick={handlePromoRedeem}
                  >
                    Redeem
                  </button>
                </div>
                {promoError && <div className="home-promo-error">{promoError}</div>}
              </div>
            )}
          </div>
      </div>

      {modalOpen && <SubscribeModal onClose={() => setModalOpen(false)} session={session} />}
      <Footer />
    </div>
  );
}

export default Home;
